import { useEffect, useState } from "react";
import { useTranslation } from "../i18n";
import { motion, AnimatePresence } from "framer-motion";
import { RiCloseLine, RiDeleteBinLine, RiImageLine } from "react-icons/ri";
import type { Job } from "../types/job";
import { jobErrorText } from "../lib/jobError";
import { formatModel, statusText } from "../lib/jobDisplay";

interface TextToImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  job: Job;
  onApprove: (jobId: Job) => Promise<void>;
  onDecline: (jobId: string) => Promise<void>;
  onDismiss?: (jobId: string) => Promise<void>;
  showHistory?: boolean;
}

export default function TextToImageModal({
  isOpen,
  onClose,
  job,
  onApprove,
  onDecline,
  onDismiss,
  showHistory = false,
}: TextToImageModalProps) {
  const { t, i18n } = useTranslation();
  const [isApproving, setIsApproving] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);

  // Üretilen görsel: resultUrl, yoksa generatedImages / result.images / *.imageUrl
  const firstImg = job.result?.images?.[0];
  const imageUrl =
    job.resultUrl ||
    (job.metadata?.generatedImages?.[0]?.url as string | undefined) ||
    (typeof firstImg === "string" ? firstImg : (firstImg?.url as string | undefined)) ||
    (job.result?.imageUrl as string | undefined) ||
    (job.metadata?.imageUrl as string | undefined) ||
    "";

  // Esc closes the modal (same callback as the X). Only attached while mounted.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  const handleApprove = async () => {
    try {
      setIsApproving(true);
      await onApprove(job);
      onClose();
    } finally {
      setIsApproving(false);
    }
  };

  const handleDecline = async () => {
    try {
      setIsDeclining(true);
      await onDecline(job.id);
      onClose();
    } finally {
      setIsDeclining(false);
    }
  };

  const handleDismiss = async () => {
    if (onDismiss) {
      await onDismiss(job.id);
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-60"
            onClick={onClose}
          />

          <motion.div
            initial={{ x: 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 420, damping: 32 }}
            className="fixed top-16 right-4 bottom-4 w-[420px] max-w-[90vw] rounded-lg border border-white/20 bg-white shadow-2xl z-70 overflow-hidden flex flex-col"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-fuchsia-500/5 pointer-events-none" />

              {/* Header */}
              <div className="relative flex items-center justify-between p-4 border-b border-black/5">
                <div className="space-y-0.5">
                  <p className="text-xs tracking-[0.08em] text-gray-500">
                    {t("modal.jobPreviewUpper")}
                  </p>
                  <h2 className="text-lg font-semibold text-gray-800">
                    {/* job.name is a hardcoded Turkish "Resim oluşturma" — use the catalog. */}
                    {t("jobNames.text_to_image")}
                  </h2>
                </div>
                <div className="flex items-center gap-1">
                  {onDismiss && !showHistory && (
                    <button
                      onClick={handleDismiss}
                      className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                      title={t("actions.dismiss")}
                    >
                      <RiDeleteBinLine className="w-4 h-4 text-gray-600 hover:text-red-600" />
                    </button>
                  )}
                  <button
                    onClick={onClose}
                    className="p-1.5 hover:bg-black/5 rounded-lg transition-colors"
                  >
                    <RiCloseLine className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>

              {/* Content: üstte prompt / altta image (dar sağ panel için dikey) */}
              <div className="relative flex-1 overflow-y-auto p-4">
                <div className="grid grid-cols-1 gap-4">
                  {/* Sol: Prompt */}
                  <div className="flex flex-col">
                    <h3 className="text-[11px] font-semibold text-gray-600 uppercase tracking-[0.08em] mb-2">
                      {t("modal.promptUpper")}
                    </h3>
                    <div className="flex-1 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap overflow-y-auto">
                      {job.prompt || <span className="text-gray-400">—</span>}
                    </div>
                  </div>

                  {/* Sağ: Üretilen görsel */}
                  <div className="flex flex-col">
                    <h3 className="text-[11px] font-semibold text-gray-600 uppercase tracking-[0.08em] mb-2">
                      {t("modal.result")}
                    </h3>
                    <div className="flex-1 rounded-lg border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center min-h-[260px]">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={t("modal.result")}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="flex flex-col items-center text-gray-300">
                          <RiImageLine className="w-12 h-12 mb-2" />
                          <span className="text-xs">{t("status.processing")}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {job.error && (
                  <div className="mt-4 border border-red-200 rounded-lg p-3 bg-red-50 text-sm">
                    <p className="text-red-700 text-[11px] uppercase tracking-[0.08em] mb-1">
                      {t("jobCard.error")}
                    </p>
                    <p className="text-red-700 text-xs leading-snug">{jobErrorText(job, t)}</p>
                  </div>
                )}

                {/* Parameters */}
                <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500 text-[11px] uppercase tracking-[0.08em]">
                      {t("jobCard.model")}
                    </p>
                    <p className="text-gray-800 font-semibold break-words">
                      {formatModel(job.model, t) || t("status.unknown")}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-[11px] uppercase tracking-[0.08em]">
                      {t("jobCard.created")}
                    </p>
                    <p className="text-gray-800 font-semibold">
                      {new Date(job.createdAt).toLocaleDateString(
                        i18n.language === "tr" ? "tr-TR" : "en-US"
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-[11px] uppercase tracking-[0.08em]">
                      {t("jobCard.status")}
                    </p>
                    <p className="text-gray-800 font-semibold capitalize">
                      {statusText(job.status, t, i18n)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              {!showHistory && (
                <div className="relative border-t border-gray-200 px-4 py-3 flex gap-2 bg-white">
                  <button
                    onClick={handleDecline}
                    disabled={isDeclining || isApproving}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-white hover:bg-red-50 text-red-600 text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-red-200"
                  >
                    {isDeclining ? t("actions.declining") : t("actions.decline")}
                  </button>
                  {job.status === "inspect" && (
                    <button
                      onClick={handleApprove}
                      disabled={isApproving || isDeclining}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                    >
                      {isApproving ? t("actions.approving") : t("actions.approve")}
                    </button>
                  )}
                </div>
              )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
